/**
 * Cart Drawer Components - Simplified
 * 
 * Includes:
 * - CartProgressBar: Three-milestone progress tracking (1/2/3 packs)
 * - SubscribeSaveToggle: Converts items between one-time and subscription
 * - SubscriptionModal: Info popup for subscription details
 * 
 * Removed: GiftSelector (no longer needed)
 */

// ============================================
// CART PROGRESS BAR CLASS
// ============================================
class CartProgressBar {
  constructor() {
    this.progressBar = document.querySelector('[data-cart-progress-bar]');
    if (!this.progressBar) return;

    this.messageElement = this.progressBar.querySelector('[data-progress-message]');
    this.messageText = this.progressBar.querySelector('.cart-progress-bar__message-text');
    this.progressFill = this.progressBar.querySelector('[data-progress-fill]');

    // Milestone elements
    this.milestone1 = this.progressBar.querySelector('[data-milestone="pack-1"]');
    this.milestone2 = this.progressBar.querySelector('[data-milestone="pack-2"]');
    this.milestone3 = this.progressBar.querySelector('[data-milestone="pack-3"]');

    this.milestone1Icon = this.milestone1?.querySelector('.cart-progress-bar__icon');
    this.milestone2Icon = this.milestone2?.querySelector('.cart-progress-bar__icon');
    this.milestone3Icon = this.milestone3?.querySelector('.cart-progress-bar__icon');

    // Thresholds
    this.MILESTONE_1_THRESHOLD = 1;
    this.MILESTONE_2_THRESHOLD = 2;
    this.MILESTONE_3_THRESHOLD = 3;

    this.init();
  }

  init() {
    // Listen for cart updates via Shopify's pub/sub system
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
    }
    this.updateFromInitialData();
  }

  updateFromInitialData() {
    const dataElement = this.progressBar?.querySelector('[data-cart-packs]');
    if (dataElement) {
      const totalPacks = parseInt(dataElement.dataset.cartPacks, 10) || 0;
      this.updateProgress(totalPacks);
    }
  }

  async onCartUpdate() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      const totalPacks = this.calculateTotalPacks(cart.items);
      this.updateProgress(totalPacks);
    } catch (error) {
      console.error('CartProgressBar: Error fetching cart', error);
    }
  }

  calculateTotalPacks(items) {
    let totalPacks = 0;
    items.forEach(item => {
      const variantTitle = item.variant_title || '';
      const packMatch = variantTitle.match(/(\d+)\s*pack/i);
      if (packMatch) {
        totalPacks += parseInt(packMatch[1], 10) * item.quantity;
      } else {
        totalPacks += item.quantity;
      }
    });
    return totalPacks;
  }

  updateProgress(totalPacks) {
    const hasMilestone1 = totalPacks >= this.MILESTONE_1_THRESHOLD;
    const hasMilestone2 = totalPacks >= this.MILESTONE_2_THRESHOLD;
    const hasMilestone3 = totalPacks >= this.MILESTONE_3_THRESHOLD;

    // Update milestone states
    this.toggleMilestoneActive(this.milestone1Icon, this.milestone1, hasMilestone1);
    this.toggleMilestoneActive(this.milestone2Icon, this.milestone2, hasMilestone2);
    this.toggleMilestoneActive(this.milestone3Icon, this.milestone3, hasMilestone3);

    // Update progress fill
    this.updateProgressFill(totalPacks);

    // Update message
    this.updateMessage(totalPacks, hasMilestone2);
  }

  updateProgressFill(totalPacks) {
    // Re-query in case DOM was replaced
    let fillElement = this.progressFill;
    if (!fillElement || !document.contains(fillElement)) {
      fillElement = document.querySelector('[data-progress-fill]');
      this.progressFill = fillElement;
    }
    
    if (!fillElement) {
      console.warn('CartProgressBar: Fill element not found');
      return;
    }

    let fillPercent = 0;
    if (totalPacks >= 3) {
      fillPercent = 100;
    } else if (totalPacks === 2) {
      fillPercent = 66;
    } else if (totalPacks === 1) {
      fillPercent = 33;
    }

    fillElement.style.width = fillPercent + '%';
    console.log('CartProgressBar: Fill updated to', fillPercent + '%', 'for', totalPacks, 'packs');
    console.log('CartProgressBar: Fill element styles:', {
      width: fillElement.style.width,
      computedWidth: getComputedStyle(fillElement).width,
      parent: fillElement.parentElement?.className
    });
  }

  toggleMilestoneActive(iconElement, milestoneElement, isActive) {
    if (!iconElement || !milestoneElement) return;
    iconElement.classList.toggle('cart-progress-bar__icon--active', isActive);
    milestoneElement.classList.toggle('cart-progress-bar__milestone--active', isActive);
  }

  updateMessage(totalPacks, isSuccess) {
    if (!this.messageElement || !this.messageText) return;

    let message = '';

    if (totalPacks >= 3) {
      message = '🎉 <strong>Maximum savings unlocked!</strong> You\'re saving 15% + FREE shipping!';
    } else if (totalPacks === 2) {
      message = 'You\'re saving <strong>10%</strong>! Add <strong>1 more pack</strong> for 15% off!';
    } else if (totalPacks === 1) {
      message = 'Add <strong>1 more pack</strong> to save 10% + get FREE shipping!';
    } else {
      message = 'Add a pack to get started!';
    }

    this.messageText.innerHTML = message;
    this.messageElement.classList.toggle('cart-progress-bar__message--success', isSuccess);
  }
}


// ============================================
// SUBSCRIBE & SAVE TOGGLE CLASS
// ============================================
class SubscribeSaveToggle {
  constructor() {
    this.sellingPlanIds = {
      '4week': null,  // Will be set from data attributes
      '8week': null
    };
    this.currentFrequency = '4week';
    this.isProcessing = false;
    this.container = null;
    this.checkbox = null;
    this.frequencySelect = null;

    this.init();
  }

  init() {
    this.container = document.querySelector('[data-subscribe-toggle]');
    if (!this.container) return;

    this.checkbox = this.container.querySelector('[data-subscribe-checkbox]');
    this.frequencySelect = this.container.querySelector('[data-frequency-select]');
    
    if (!this.checkbox) return;

    // Get selling plan IDs from data attributes
    this.sellingPlanIds['4week'] = this.container.dataset['plan-4week'];
    this.sellingPlanIds['8week'] = this.container.dataset['plan-8week'];

    // Prevent duplicate listeners on checkbox
    if (this.checkbox.dataset.listenerAttached !== 'true') {
      this.checkbox.dataset.listenerAttached = 'true';
      this.checkbox.addEventListener('change', () => this.handleToggle());
    }

    // Prevent duplicate listeners on frequency select
    if (this.frequencySelect && this.frequencySelect.dataset.listenerAttached !== 'true') {
      this.frequencySelect.dataset.listenerAttached = 'true';
      this.frequencySelect.addEventListener('change', () => this.handleFrequencyChange());
    }

    console.log('SubscribeSaveToggle: Initialized with plans', this.sellingPlanIds);
  }

  isValid() {
    if (!this.container || !this.checkbox) return false;
    const currentCheckbox = document.querySelector('[data-subscribe-checkbox]');
    return currentCheckbox === this.checkbox;
  }

  getActiveSellingPlanId() {
    return this.sellingPlanIds[this.currentFrequency];
  }

  async handleToggle() {
    if (this.isProcessing) return;
    
    this.isProcessing = true;
    this.container.classList.add('subscribe-save-toggle--loading');
    
    // Get current frequency from dropdown
    if (this.frequencySelect) {
      this.currentFrequency = this.frequencySelect.value;
    }
    
    const enableSubscription = this.checkbox.checked;
    console.log('SubscribeSaveToggle: Toggle to', enableSubscription ? 'subscription' : 'one-time', 'frequency:', this.currentFrequency);
    
    try {
      const cart = await fetch('/cart.js').then(r => r.json());
      
      for (const item of cart.items) {
        const hasSubscription = !!item.selling_plan_allocation;
        
        if (enableSubscription && !hasSubscription) {
          await this.convertToSubscription(item);
        } else if (!enableSubscription && hasSubscription) {
          await this.convertToOneTime(item);
        }
      }
      
      await refreshCartDrawer();
      
    } catch (error) {
      console.error('SubscribeSaveToggle: Error', error);
      this.checkbox.checked = !this.checkbox.checked;
    } finally {
      this.isProcessing = false;
      this.container?.classList.remove('subscribe-save-toggle--loading');
    }
  }

  async handleFrequencyChange() {
    if (this.isProcessing) return;
    
    const newFrequency = this.frequencySelect.value;
    console.log('SubscribeSaveToggle: Frequency changed to', newFrequency);
    
    // Only update if subscription is active
    if (!this.checkbox.checked) {
      this.currentFrequency = newFrequency;
      return;
    }
    
    this.isProcessing = true;
    this.container.classList.add('subscribe-save-toggle--loading');
    
    try {
      const cart = await fetch('/cart.js').then(r => r.json());
      
      // Update frequency for all subscription items
      for (const item of cart.items) {
        if (item.selling_plan_allocation) {
          await this.updateSubscriptionFrequency(item, newFrequency);
        }
      }
      
      this.currentFrequency = newFrequency;
      await refreshCartDrawer();
      
    } catch (error) {
      console.error('SubscribeSaveToggle: Error changing frequency', error);
      // Revert dropdown
      this.frequencySelect.value = this.currentFrequency;
    } finally {
      this.isProcessing = false;
      this.container?.classList.remove('subscribe-save-toggle--loading');
    }
  }

  async updateSubscriptionFrequency(item, newFrequency) {
    const newSellingPlanId = this.sellingPlanIds[newFrequency];
    
    // Remove the current subscription item
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: item.key, quantity: 0 })
    });

    // Add back with new selling plan
    await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: item.variant_id,
        quantity: item.quantity,
        selling_plan: newSellingPlanId,
        properties: item.properties || {}
      })
    });
  }

  async convertToSubscription(item) {
    const sellingPlanId = this.getActiveSellingPlanId();
    
    // Remove the one-time item
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: item.key, quantity: 0 })
    });

    // Add back with selling plan
    await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: item.variant_id,
        quantity: item.quantity,
        selling_plan: sellingPlanId,
        properties: item.properties || {}
      })
    });
  }

  async convertToOneTime(item) {
    // Remove the subscription item
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: item.key, quantity: 0 })
    });

    // Add back without selling plan
    await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: item.variant_id,
        quantity: item.quantity,
        properties: item.properties || {}
      })
    });
  }
}


// ============================================
// SUBSCRIPTION MODAL CLASS
// ============================================
class SubscriptionModal {
  constructor() {
    this.modal = document.querySelector('[data-subscription-modal]');
    this.infoBtn = document.querySelector('[data-subscription-info-btn]');
    this.closeButtons = document.querySelectorAll('[data-modal-close]');

    this.init();
  }

  init() {
    if (!this.modal) return;

    // Open button
    if (this.infoBtn && this.infoBtn.dataset.listenerAttached !== 'true') {
      this.infoBtn.dataset.listenerAttached = 'true';
      this.infoBtn.addEventListener('click', () => this.open());
    }

    // Close buttons (X and overlay)
    this.closeButtons.forEach(btn => {
      if (btn.dataset.listenerAttached !== 'true') {
        btn.dataset.listenerAttached = 'true';
        btn.addEventListener('click', () => this.close());
      }
    });

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.modal.hidden) {
        this.close();
      }
    });

    console.log('SubscriptionModal: Initialized');
  }

  open() {
    this.modal.hidden = false;
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.modal.hidden = true;
    document.body.style.overflow = '';
  }
}


// ============================================
// SHARED FUNCTIONS
// ============================================
async function refreshCartDrawer() {
  try {
    const response = await fetch(window.location.pathname + '?sections=cart-drawer');
    const sections = await response.json();

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && sections['cart-drawer']) {
      const drawerInner = cartDrawer.querySelector('.drawer__inner');
      const newContent = new DOMParser()
        .parseFromString(sections['cart-drawer'], 'text/html')
        .querySelector('.drawer__inner');

      if (drawerInner && newContent) {
        drawerInner.innerHTML = newContent.innerHTML;
        
        // Reinit after DOM update
        setTimeout(initAllComponents, 100);
      }
    }
  } catch (error) {
    console.error('Error refreshing cart drawer', error);
  }
}

// Track initialization state
let isInitializing = false;

function initAllComponents() {
  if (isInitializing) return;
  isInitializing = true;

  window.cartProgressBar = new CartProgressBar();
  window.subscribeSaveToggle = new SubscribeSaveToggle();
  window.subscriptionModal = new SubscriptionModal();

  isInitializing = false;
}


// ============================================
// SMART OBSERVER
// ============================================
function setupSmartObserver() {
  const cartDrawer = document.querySelector('cart-drawer');
  if (!cartDrawer) return;

  let debounceTimer = null;
  
  const observer = new MutationObserver((mutations) => {
    let hasSignificantChange = false;
    
    for (const mutation of mutations) {
      if (mutation.type === 'childList') {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.classList?.contains('drawer__inner') || 
                node.querySelector?.('[data-subscribe-toggle]') ||
                node.querySelector?.('[data-cart-progress-bar]')) {
              hasSignificantChange = true;
              break;
            }
          }
        }
      }
      if (hasSignificantChange) break;
    }

    if (hasSignificantChange) {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        // Only reinit if toggle reference is stale
        if (window.subscribeSaveToggle && !window.subscribeSaveToggle.isValid()) {
          console.log('SmartObserver: Reinitializing stale components');
          initAllComponents();
        }
      }, 150);
    }
  });

  observer.observe(cartDrawer, { childList: true, subtree: true });
}


// ============================================
// INITIALIZATION
// ============================================
document.addEventListener('DOMContentLoaded', () => {
  initAllComponents();
  setupSmartObserver();
});

// Fallback for lazy-loaded cart drawer
setTimeout(() => {
  if (!window.subscribeSaveToggle?.container) {
    initAllComponents();
    setupSmartObserver();
  }
}, 1000);
